'use client';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useLazyQuery } from '@parkchirp/api-client';
import { HourlyValidation, HourlyValidationMethod, HourlyValidationModifier, Rate } from 'parkchirp-shared/types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks';
import { LIST_HOURLY_VALIDATIONS_BY_FACILITY_ID_V2 } from '../../queries';
import { HourlyQueryContextEnum } from '../../types';

type Option = HourlyValidationMethod & {
	validation: HourlyValidation & { modifier: HourlyValidationModifier & { rate: Rate } };
} & {
	label: string;
	value: string;
	key: string;
};

interface ValidationMethodAutocompleteProps {
	facilityId: string;
	validationMethodId?: string;
	validationMethodText?: string;
	label?: string;
	disabled?: boolean;
	disableClearable?: boolean;
	error?: boolean;
	required?: boolean;
	onChange?: (value: Option) => void;
	loadingDependency?: boolean;
	ignoredOptions?: string[];
	size?: 'small' | 'medium';
}

export const ValidationMethodAutocomplete: FC<ValidationMethodAutocompleteProps> = ({
	facilityId,
	validationMethodId,
	validationMethodText,
	label,
	disabled = false,
	disableClearable = false,
	error = false,
	required = false,
	onChange,
	loadingDependency = false,
	ignoredOptions = [],
	size = 'medium',
}) => {
	const defaultOption: Partial<Option> = useMemo(() => {
		if (!facilityId || !validationMethodId) {
			return null;
		}

		return {
			label: validationMethodText,
			value: validationMethodId,
			key: validationMethodId,
			text: validationMethodText,
		};
	}, [validationMethodId, validationMethodText, facilityId]);

	const [options, setOptions] = useState<Option[]>(defaultOption ? [defaultOption as Option] : []);

	const [searchTerm, setSearchTerm] = useState('');
	const [open, setOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState<Option>(defaultOption as Option);

	useEffect(() => {
		setSelectedOption(defaultOption as Option);
	}, [defaultOption]);

	const debouncedOnChange = useDebounce(setSearchTerm, 800);

	const onInputChange = useCallback(
		(value: string) => {
			if (open && value !== searchTerm) {
				debouncedOnChange(value);
			}
		},
		[open, searchTerm, debouncedOnChange],
	);

	const onAutocompleteChange = useCallback(
		(option: Option) => {
			if (!option) {
				setSearchTerm(null);
			}
			setSelectedOption(option);
			onChange(option);
		},
		[onChange],
	);

	const queryFilters = useMemo(() => {
		if (!searchTerm) {
			return [];
		}

		return [
			{
				propertyName: 'text',
				op: 'contains',
				value: `${searchTerm}`,
			},
			{
				propertyName: 'name',
				op: 'contains',
				value: `${searchTerm}`,
			},
		];
	}, [searchTerm]);

	const [search, { loading }] = useLazyQuery(LIST_HOURLY_VALIDATIONS_BY_FACILITY_ID_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'no-cache',
	});

	useEffect(() => {
		if (!open && !selectedOption) {
			setSearchTerm(null);
		}
	}, [open, selectedOption]);

	useEffect(() => {
		search({
			variables: {
				offset: 0,
				limit: 50,
				queryContext: HourlyQueryContextEnum.AUTOCOMPLETE,
				filters: queryFilters,
				facilityId,
				order: {
					field: 'text',
					sort: 'asc',
				},
			},
			onCompleted: (data) => {
				const newOptions: Option[] = data.listHourlyValidationsByFacilityId.reduce((acc, validation) => {
					acc.push({
						...validation,
						label: validation.text,
						value: validation.id,
						key: validation.id,
					});

					return acc;
				}, []);
				setOptions(newOptions);
			},
		});
	}, [search, queryFilters, facilityId]);

	const isLoading = loading || loadingDependency;

	return (
		<Autocomplete
			open={open}
			onOpen={() => {
				setOpen(true);
			}}
			onClose={() => {
				setOpen(false);
			}}
			value={selectedOption}
			onKeyDown={(event) => {
				event.stopPropagation();
			}}
			onChange={(_, value) => {
				onAutocompleteChange(value);
			}}
			onInputChange={(_, value, reason) => {
				if (reason === 'input') {
					onInputChange(value);
				}
			}}
			options={options}
			getOptionDisabled={(option) => ignoredOptions.includes(option.value)}
			isOptionEqualToValue={(option, { value }) => option.value === value}
			loading={isLoading}
			filterOptions={(x) => x}
			forcePopupIcon={false}
			fullWidth
			renderOption={(props, option) => (
				<li {...props} key={option.key} style={{ whiteSpace: 'nowrap' }}>
					{option.label}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || ''}
					required={required}
					error={error}
					placeholder='Start Typing...'
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color='inherit' size={20} data-cy='loading-indicator' />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			disabled={disabled}
			readOnly={disabled}
			disableClearable={disableClearable}
			sx={{
				'& .MuiInputBase-root': {
					paddingRight: '39px !important',
				},
			}}
			size={size}
		/>
	);
};
